export const DATABASE_NAME = 'menucheff-desktop';
export const DATABASE_VERSION = 1;

const BOOLEAN_FIELDS = new Set(`Ativo CardapioAtivo IntegracaoUnicheff CatalogoEmpresas
ExibirPizzasGrupo DesabilitarSabores IsPizza IsAdicional UsaHorario UsaSaboresSelecionados
BloqueadoVenda PermiteDsctProduto ProdutoFavorito Disponivel Fracionado FreteGratis
HasDesconto HasDescontoPercentual useMenu ignoreGroups enabled firstContact isFavorite
isPending isProgress isDelivered isPickUp isFinished isResume`.split(/\s+/));
const MONEY_FIELDS = new Set(['PrecoVenda', 'Preco', 'PrecoDesconto', 'ValorDesconto', 'precoVenda']);

export function normalizeEntity(entity) {
  const result = { ...entity };
  Object.keys(result).forEach((key) => {
    const value = result[key];
    if (value === null || value === undefined) return;
    if (['NrContrato', 'companyId'].includes(key)) {
      if (!Number.isSafeInteger(Number(value)) || Number(value) < 1) throw new Error('Contrato inválido');
      result[key] = Number(value);
    } else if (BOOLEAN_FIELDS.has(key)) {
      if (![true, false, 0, 1, '0', '1', 'true', 'false'].includes(value)) {
        throw new Error(`Booleano inválido: ${key}`);
      }
      result[key] = value === true || value === 1 || value === '1' || value === 'true';
    } else if (MONEY_FIELDS.has(key)) {
      const decimal = String(value);
      if (!/^-?\d+(?:\.\d+)?$/.test(decimal)) throw new Error(`Valor monetário inválido: ${key}`);
      result[key] = decimal.includes('.') ? decimal : `${decimal}.00`;
    } else if (['createdAt', 'updatedAt', 'DataCadastro'].includes(key)) {
      // SQL timestamps from raw queries are UTC, just like hydrated API dates.
      const date = typeof value === 'string' && /^\d{4}-\d\d-\d\d \d\d:/.test(value)
        ? `${value.replace(' ', 'T')}Z` : value;
      const timestamp = new Date(date);
      if (Number.isNaN(timestamp.getTime())) throw new Error(`Data inválida: ${key}`);
      result[key] = timestamp.toISOString();
    }
  });
  return result;
}

function requireFields(entity, fields, label) {
  if (!entity || typeof entity !== 'object' || Array.isArray(entity)) throw new Error(`${label} inválido`);
  fields.forEach((field) => {
    if (entity[field] === null || entity[field] === undefined) throw new Error(`${label}.${field} ausente`);
  });
}

function requireCompany(entity, companyId, field) {
  if (Number(entity[field]) !== companyId) throw new Error('Entidade pertence a outra empresa');
}

export function validateSnapshot(snapshot) {
  const { companyId, company, chatbot, answers, groups, paymentMethods, schedules } = snapshot;
  if (!Number.isSafeInteger(companyId) || companyId < 1) throw new Error('Contrato inválido');
  requireFields(company, ['Id', 'NrContrato'], 'company');
  requireCompany(company, companyId, 'NrContrato');
  requireFields(chatbot, ['Id', 'companyId', 'interval', 'intervalMessage', 'useMenu',
    'ignoreGroups', 'enabled', 'createdAt', 'updatedAt'], 'chatbot');
  requireCompany(chatbot, companyId, 'companyId');
  [answers, groups, paymentMethods, schedules].forEach((list) => {
    if (!Array.isArray(list)) throw new Error('Lista obrigatória ausente');
  });
  const validateList = (list, fields, label) => {
    const ids = new Set();
    list.forEach((entry) => {
      requireFields(entry, fields, label);
      const id = JSON.stringify(entry.Id);
      if (ids.has(id)) throw new Error(`ID duplicado em ${label}`);
      ids.add(id);
    });
  };
  validateList(answers, ['Id', 'chatbotId', 'name', 'text', 'isPending', 'isProgress',
    'isDelivered', 'isPickUp', 'isFinished', 'isResume', 'Ativo'], 'answer');
  answers.forEach((answer) => {
    if (!chatbot || answer.chatbotId !== chatbot.Id) throw new Error('Resposta sem chatbot correspondente');
  });
  validateList(groups, ['Id', 'IdEmpresa', 'CodigoPDV', 'Descricao', 'Sequencia'], 'group');
  const productIds = new Set();
  const sizeIds = new Set();
  groups.forEach((group) => {
    requireCompany(group, companyId, 'IdEmpresa');
    if (!Array.isArray(group.products)) throw new Error('Produtos ausentes');
    validateList(group.products, ['Id', 'Codigo', 'IdEmpresa'], 'product');
    group.products.forEach((product) => {
      requireCompany(product, companyId, 'IdEmpresa');
      if (String(product.Grupo) !== String(group.Id)) throw new Error('Grupo do produto inválido');
      if (productIds.has(product.Id)) throw new Error('Produto duplicado');
      productIds.add(product.Id);
      if (!Array.isArray(product.sizes)) throw new Error('Tamanhos ausentes');
      validateList(product.sizes, ['Id', 'IdEmpresa', 'IdProduto'], 'size');
      product.sizes.forEach((size) => {
        requireCompany(size, companyId, 'IdEmpresa');
        if (size.IdProduto !== product.Id) throw new Error('Produto do tamanho inválido');
        if (sizeIds.has(size.Id)) throw new Error('Tamanho duplicado');
        sizeIds.add(size.Id);
      });
    });
  });
  validateList(paymentMethods, ['Id', 'Descricao', 'createdAt', 'updatedAt'], 'paymentMethod');
  validateList(schedules, ['Id', 'IdEmpresa', 'DiaSemana', 'Inicio', 'Fim', 'createdAt', 'updatedAt'], 'schedule');
  schedules.forEach((schedule) => requireCompany(schedule, companyId, 'IdEmpresa'));
  return snapshot;
}

// API ordering and property order must not create artificial revisions.
export function canonicalString(value) {
  if (Array.isArray(value)) {
    const sorted = value.every((entry) => entry && typeof entry === 'object' && entry.Id !== undefined)
      ? [...value].sort((a, b) => {
        const left = JSON.stringify(a?.Id ?? a), right = JSON.stringify(b?.Id ?? b);
        return left < right ? -1 : left > right ? 1 : 0;
      }) : value;
    return `[${sorted.map(canonicalString).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonicalString(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

export function readSession(storage = localStorage) {
  try {
    const token = storage.getItem('@delivery-Token');
    const userEncoded = storage.getItem('@delivery-User');
    if (!token || !userEncoded || ['undefined', 'null'].includes(token)) return null;
    const user = JSON.parse(atob(userEncoded));
    const socketRaw = storage.getItem('@socket-token');
    const socketUser = socketRaw ? JSON.parse(socketRaw) : user;
    const companyId = Number(socketUser.IdEmpresa);
    if (!Number.isSafeInteger(companyId) || companyId < 1 || Number(user.IdEmpresa) !== companyId || !user.Id) return null;
    return { companyId, token, userEncoded, user: {
      id: user.Id, login: user.Login ?? user.login ?? null, name: user.Nome ?? user.name ?? null,
    } };
  } catch (_) { return null; }
}
